import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Users, Clock, MapPin, Plus, Minus, Baby, User, Crown } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { supabase } from '../utils/supabase';
import { redirectToWompi } from '../utils/wompi';
import { formatCOP, priceBreakdown } from '../utils/format';
type Sport = 'futbol' | 'padel' | 'tenis';
type FutbolCategory = 'adultos' | 'ninos';
interface Captain {
  name: string;
  email: string;
  phone: string;
  cedula: string;
}
interface AdultoPlayer {
  name: string;
  cedula: string;
  age: string;
}
interface NinoPlayer {
  name: string;
  ti: string;
  age: string;
  responsableName: string;
  responsablePhone: string;
}
interface PadelPlayer {
  name: string;
  email: string;
  phone: string;
  cedula: string;
}
interface TenisPlayer {
  name: string;
  email: string;
  phone: string;
  cedula: string;
}
const emptyCaptain = (): Captain => ({
  name: '',
  email: '',
  phone: '',
  cedula: ''
});
const emptyAdulto = (): AdultoPlayer => ({
  name: '',
  cedula: '',
  age: ''
});
const emptyNino = (): NinoPlayer => ({
  name: '',
  ti: '',
  age: '',
  responsableName: '',
  responsablePhone: ''
});
const emptyPadel = (): PadelPlayer => ({
  name: '',
  email: '',
  phone: '',
  cedula: ''
});
const emptyTenis = (): TenisPlayer => ({
  name: '',
  email: '',
  phone: '',
  cedula: ''
});
const ADULTO_PRICE = 65000;
const NINO_PRICE = 45000;
const PADEL_PRICE = 60000;
const TENIS_PRICE = 70000;
const TERMS_VERSION = '2026-05-01';
const SOURCE_TAG_ADULTOS = 'deporte-futbol-adultos';
const SOURCE_TAG_NINOS = 'deporte-futbol-ninos';
const SOURCE_TAG_PADEL = 'deporte-padel';
const SOURCE_TAG_TENIS = 'deporte-tenis';
export function DeportesPage() {
  const [activeSport, setActiveSport] = useState<Sport>('futbol');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [futbolCategory, setFutbolCategory] = useState<FutbolCategory>('adultos');
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  // Adultos team
  const [teamNameAdultos, setTeamNameAdultos] = useState('');
  const [captainAdultos, setCaptainAdultos] = useState<Captain>(emptyCaptain());
  const [adultos, setAdultos] = useState<AdultoPlayer[]>([emptyAdulto(), emptyAdulto(), emptyAdulto(), emptyAdulto(), emptyAdulto()]);
  const [acceptedTermsAdultosAt, setAcceptedTermsAdultosAt] = useState<string | null>(null);
  const [acceptedReglamentoAdultosAt, setAcceptedReglamentoAdultosAt] = useState<string | null>(null);
  const [acceptedHabeasAdultosAt, setAcceptedHabeasAdultosAt] = useState<string | null>(null);
  // Niños team
  const [teamNameNinos, setTeamNameNinos] = useState('');
  const [captainNinos, setCaptainNinos] = useState<Captain>(emptyCaptain());
  const [ninos, setNinos] = useState<NinoPlayer[]>([emptyNino(), emptyNino(), emptyNino(), emptyNino(), emptyNino()]);
  const [acceptedTermsNinosAt, setAcceptedTermsNinosAt] = useState<string | null>(null);
  const [acceptedReglamentoNinosAt, setAcceptedReglamentoNinosAt] = useState<string | null>(null);
  const [acceptedHabeasNinosAt, setAcceptedHabeasNinosAt] = useState<string | null>(null);
  // Pádel — solo 2 o 4 jugadores
  const [padelPlayers, setPadelPlayers] = useState<PadelPlayer[]>([emptyPadel(), emptyPadel()]);
  const [acceptedTermsPadelAt, setAcceptedTermsPadelAt] = useState<string | null>(null);
  const [acceptedReglamentoPadelAt, setAcceptedReglamentoPadelAt] = useState<string | null>(null);
  const [acceptedHabeasPadelAt, setAcceptedHabeasPadelAt] = useState<string | null>(null);
  // Tenis — 5 jugadores fijos
  const [tenisPlayers, setTenisPlayers] = useState<TenisPlayer[]>([emptyTenis(), emptyTenis(), emptyTenis(), emptyTenis(), emptyTenis()]);
  const [acceptedTermsTenisAt, setAcceptedTermsTenisAt] = useState<string | null>(null);
  const [acceptedReglamentoTenisAt, setAcceptedReglamentoTenisAt] = useState<string | null>(null);
  const [acceptedHabeasTenisAt, setAcceptedHabeasTenisAt] = useState<string | null>(null);
  const [selectedSlotAdultos, setSelectedSlotAdultos] = useState<number | null>(null);
  const [selectedSlotNinos, setSelectedSlotNinos] = useState<number | null>(null);
  const [selectedSlotTenis, setSelectedSlotTenis] = useState<number | null>(null);
  const [takenSlotsAdultos, setTakenSlotsAdultos] = useState<number[]>([]);
  const [takenSlotsNinos, setTakenSlotsNinos] = useState<number[]>([]);
  const [takenSlotsTenis, setTakenSlotsTenis] = useState<number[]>([]);
  useEffect(() => {
    // Same status normalization as ExpositoresPage — tolerant of paid/approved variants
    const SOLD_STATUSES = ['paid', 'approved', 'complete', 'completed'];
    const RESERVED_STATUSES = ['pending_payment', 'pending'];
    const isBlocking = (raw: string | null | undefined) => {
      if (!raw) return false;
      const s = raw.toLowerCase().trim();
      return SOLD_STATUSES.includes(s) || RESERVED_STATUSES.includes(s);
    };
    const fetchSlots = async () => {
      const {
        data
      } = await supabase.from('sports_team_registrations').select('category, team_slot, status').not('team_slot', 'is', null);
      if (data) {
        const blocking = data.filter(d => isBlocking(d.status));
        setTakenSlotsAdultos(blocking.filter(d => d.category === 'adultos').map(d => d.team_slot));
        setTakenSlotsNinos(blocking.filter(d => d.category === 'ninos').map(d => d.team_slot));
        setTakenSlotsTenis(blocking.filter(d => d.category === 'tenis').map(d => d.team_slot));
      }
    };
    fetchSlots();
    // Realtime subscription — refresh whenever a team registration changes
    const channel = supabase.channel('sports_team_registrations_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'sports_team_registrations'
    }, () => fetchSlots()).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);
  // Fetch user's IP on mount
  useEffect(() => {
    fetch('https://api.ipify.org?format=json').then(r => r.json()).then(data => setIpAddress(data?.ip || null)).catch(() => setIpAddress(null));
  }, []);
  // Adulto helpers
  const addAdulto = () => {
    if (adultos.length < 10) setAdultos([...adultos, emptyAdulto()]);
  };
  const removeAdulto = (index: number) => {
    if (adultos.length > 1) setAdultos(adultos.filter((_, i) => i !== index));
  };
  const updateAdulto = (index: number, field: keyof AdultoPlayer, value: string) => {
    const updated = [...adultos];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setAdultos(updated);
  };
  // Niño helpers
  const addNino = () => {
    if (ninos.length < 8) setNinos([...ninos, emptyNino()]);
  };
  const removeNino = (index: number) => {
    if (ninos.length > 1) setNinos(ninos.filter((_, i) => i !== index));
  };
  const updateNino = (index: number, field: keyof NinoPlayer, value: string) => {
    const updated = [...ninos];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setNinos(updated);
  };
  // Pádel helpers
  const updatePadelPlayer = (index: number, field: keyof PadelPlayer, value: string) => {
    const updated = [...padelPlayers];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setPadelPlayers(updated);
  };
  // Tenis helpers
  const updateTenisPlayer = (index: number, field: keyof TenisPlayer, value: string) => {
    const updated = [...tenisPlayers];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setTenisPlayers(updated);
  };
  const adultosTotal = adultos.length * ADULTO_PRICE;
  const ninosTotal = ninos.length * NINO_PRICE;
  const padelTotal = padelPlayers.length * PADEL_PRICE;
  const tenisTotal = tenisPlayers.length * TENIS_PRICE;
  const updateCaptainAdultos = (field: keyof Captain, value: string) => {
    setCaptainAdultos({
      ...captainAdultos,
      [field]: value
    });
  };
  const updateCaptainNinos = (field: keyof Captain, value: string) => {
    setCaptainNinos({
      ...captainNinos,
      [field]: value
    });
  };
  // Toggle helper for legal timestamps
  const toggleTs = (checked: boolean, setter: (v: string | null) => void) => {
    setter(checked ? new Date().toISOString() : null);
  };
  // ========================================================
  // HANDLER · Fútbol Adultos
  // ========================================================
  const handleAdultosRegistration = async () => {
    if (!selectedSlotAdultos) {
      toast.error('Selecciona un número de equipo');
      return;
    }
    if (!teamNameAdultos.trim()) {
      toast.error('Ingresa el nombre del equipo');
      return;
    }
    if (!captainAdultos.name.trim() || !captainAdultos.phone.trim() || !captainAdultos.email.trim()) {
      toast.error('Completa los datos del capitán (nombre, correo, celular)');
      return;
    }
    const filled = adultos.filter(p => p.name.trim());
    if (filled.length < 6) {
      toast.error('Registra al menos 6 jugadores');
      return;
    }
    if (!acceptedTermsAdultosAt || !acceptedReglamentoAdultosAt || !acceptedHabeasAdultosAt) {
      toast.error('Acepta los términos, el reglamento y el habeas data');
      return;
    }
    setIsSubmitting(true);
    const amountInCents = priceBreakdown(adultosTotal).total * 100;
    try {
      const {
        data: team,
        error: teamError
      } = await supabase.from('sports_team_registrations').insert({
        source_tag: SOURCE_TAG_ADULTOS,
        sport: 'futbol',
        category: 'adultos',
        team_slot: selectedSlotAdultos,
        team_name: teamNameAdultos.trim(),
        captain_name: captainAdultos.name.trim(),
        captain_email: captainAdultos.email.trim(),
        captain_phone: captainAdultos.phone.trim(),
        captain_cedula: captainAdultos.cedula.trim() || null,
        player_count: filled.length,
        amount_cents: amountInCents,
        payment_method: 'wompi',
        terms_version: TERMS_VERSION,
        ip_address: ipAddress,
        accepted_terms_at: acceptedTermsAdultosAt,
        accepted_reglamento_at: acceptedReglamentoAdultosAt,
        accepted_habeas_data_at: acceptedHabeasAdultosAt
      }).select().single();
      if (teamError) {
        if (teamError.code === '23505') {
          toast.error('Ese número de equipo acaba de ser tomado. Por favor elige otro.');
          setIsSubmitting(false);
          return;
        }
        throw teamError;
      }
      if (!team) throw new Error('No se pudo crear el equipo');
      const playersPayload = filled.map((p, i) => ({
        team_id: team.id,
        player_index: i + 1,
        is_captain: false,
        name: p.name.trim(),
        age: p.age ? parseInt(p.age, 10) : null,
        cedula: p.cedula.trim() || null
      }));
      const {
        error: playersError
      } = await supabase.from('sports_team_players').insert(playersPayload);
      if (playersError) {
        await supabase.from('sports_team_registrations').delete().eq('id', team.id);
        throw playersError;
      }
      toast.success('¡Equipo registrado! Redirigiendo al pago...');
      await redirectToWompi({
        reference: `FUTBOL-A-${team.id}`,
        amountInCents,
        customer: {
          email: captainAdultos.email.trim(),
          fullName: captainAdultos.name.trim(),
          phone: captainAdultos.phone.trim(),
          legalIdType: 'CC',
          legalId: captainAdultos.cedula.trim() || undefined
        },
        redirectUrl: `${window.location.origin}/gracias?id=${team.id}&table=sports_team_registrations`
      });
    } catch (err: any) {
      console.error('Error registrando equipo adultos:', err);
      const detail = err?.message || err?.details || err?.hint || 'error desconocido';
      toast.error(`No pudimos registrar el equipo: ${detail}`, {
        duration: 8000
      });
      setIsSubmitting(false);
    }
  };
  // ========================================================
  // HANDLER · Fútbol Niños
  // ========================================================
  const handleNinosRegistration = async () => {
    if (!selectedSlotNinos) {
      toast.error('Selecciona un número de equipo');
      return;
    }
    if (!teamNameNinos.trim()) {
      toast.error('Ingresa el nombre del equipo');
      return;
    }
    if (!captainNinos.name.trim() || !captainNinos.phone.trim() || !captainNinos.email.trim()) {
      toast.error('Completa los datos del adulto a cargo del equipo');
      return;
    }
    const filled = ninos.filter(p => p.name.trim());
    if (filled.length < 6) {
      toast.error('Registra al menos 6 niños');
      return;
    }
    if (!acceptedTermsNinosAt || !acceptedReglamentoNinosAt || !acceptedHabeasNinosAt) {
      toast.error('Acepta los términos, el reglamento y el habeas data');
      return;
    }
    setIsSubmitting(true);
    const amountInCents = priceBreakdown(ninosTotal).total * 100;
    try {
      const {
        data: team,
        error: teamError
      } = await supabase.from('sports_team_registrations').insert({
        source_tag: SOURCE_TAG_NINOS,
        sport: 'futbol',
        category: 'ninos',
        team_slot: selectedSlotNinos,
        team_name: teamNameNinos.trim(),
        captain_name: captainNinos.name.trim(),
        captain_email: captainNinos.email.trim(),
        captain_phone: captainNinos.phone.trim(),
        captain_cedula: captainNinos.cedula.trim() || null,
        player_count: filled.length,
        amount_cents: amountInCents,
        payment_method: 'wompi',
        terms_version: TERMS_VERSION,
        ip_address: ipAddress,
        accepted_terms_at: acceptedTermsNinosAt,
        accepted_reglamento_at: acceptedReglamentoNinosAt,
        accepted_habeas_data_at: acceptedHabeasNinosAt
      }).select().single();
      if (teamError) {
        if (teamError.code === '23505') {
          toast.error('Ese número de equipo acaba de ser tomado. Por favor elige otro.');
          setIsSubmitting(false);
          return;
        }
        throw teamError;
      }
      if (!team) throw new Error('No se pudo crear el equipo');
      const playersPayload = filled.map((p, i) => ({
        team_id: team.id,
        player_index: i + 1,
        is_captain: false,
        name: p.name.trim(),
        age: p.age ? parseInt(p.age, 10) : null,
        ti: p.ti.trim() || null,
        responsable_name: p.responsableName.trim() || null,
        responsable_phone: p.responsablePhone.trim() || null
      }));
      const {
        error: playersError
      } = await supabase.from('sports_team_players').insert(playersPayload);
      if (playersError) {
        await supabase.from('sports_team_registrations').delete().eq('id', team.id);
        throw playersError;
      }
      toast.success('¡Equipo registrado! Redirigiendo al pago...');
      await redirectToWompi({
        reference: `FUTBOL-N-${team.id}`,
        amountInCents,
        customer: {
          email: captainNinos.email.trim(),
          fullName: captainNinos.name.trim(),
          phone: captainNinos.phone.trim(),
          legalIdType: 'CC',
          legalId: captainNinos.cedula.trim() || undefined
        },
        redirectUrl: `${window.location.origin}/gracias?id=${team.id}&table=sports_team_registrations`
      });
    } catch (err: any) {
      console.error('Error registrando equipo niños:', err);
      const detail = err?.message || err?.details || err?.hint || 'error desconocido';
      toast.error(`No pudimos registrar el equipo: ${detail}`, {
        duration: 8000
      });
      setIsSubmitting(false);
    }
  };
  // ========================================================
  // HANDLER · Pádel
  // ========================================================
  const handlePadelRegistration = async () => {
    const filledPlayers = padelPlayers.filter(p => p.name.trim());
    if (filledPlayers.length < 2) {
      toast.error('Registra al menos a los 2 jugadores de la pareja');
      return;
    }
    const captain = filledPlayers[0];
    if (!captain.name.trim() || !captain.email.trim() || !captain.phone.trim()) {
      toast.error('Completa los datos del Jugador 1 (capitán): nombre, correo y celular');
      return;
    }
    if (!acceptedTermsPadelAt || !acceptedReglamentoPadelAt || !acceptedHabeasPadelAt) {
      toast.error('Acepta los términos, el reglamento y el habeas data');
      return;
    }
    setIsSubmitting(true);
    const amountInCents = priceBreakdown(padelTotal).total * 100;
    try {
      const {
        data: team,
        error: teamError
      } = await supabase.from('sports_team_registrations').insert({
        source_tag: SOURCE_TAG_PADEL,
        sport: 'padel',
        category: null,
        team_name: null,
        captain_name: captain.name.trim(),
        captain_email: captain.email.trim(),
        captain_phone: captain.phone.trim(),
        captain_cedula: captain.cedula.trim() || null,
        player_count: filledPlayers.length,
        amount_cents: amountInCents,
        payment_method: 'wompi',
        terms_version: TERMS_VERSION,
        ip_address: ipAddress,
        accepted_terms_at: acceptedTermsPadelAt,
        accepted_reglamento_at: acceptedReglamentoPadelAt,
        accepted_habeas_data_at: acceptedHabeasPadelAt
      }).select().single();
      if (teamError || !team) throw teamError || new Error('No se pudo crear la pareja');
      const playersPayload = filledPlayers.map((p, i) => ({
        team_id: team.id,
        player_index: i + 1,
        is_captain: i === 0,
        name: p.name.trim(),
        email: p.email.trim() || null,
        phone: p.phone.trim() || null,
        cedula: p.cedula.trim() || null
      }));
      const {
        error: playersError
      } = await supabase.from('sports_team_players').insert(playersPayload);
      if (playersError) {
        await supabase.from('sports_team_registrations').delete().eq('id', team.id);
        throw playersError;
      }
      toast.success('¡Pareja registrada! Redirigiendo al pago...');
      await redirectToWompi({
        reference: `PADEL-${team.id}`,
        amountInCents,
        customer: {
          email: captain.email.trim(),
          fullName: captain.name.trim(),
          phone: captain.phone.trim(),
          legalIdType: 'CC',
          legalId: captain.cedula.trim() || undefined
        },
        redirectUrl: `${window.location.origin}/gracias?id=${team.id}&table=sports_team_registrations`
      });
    } catch (err: any) {
      console.error('Error registrando pareja pádel:', err);
      const detail = err?.message || err?.details || err?.hint || 'error desconocido';
      toast.error(`No pudimos registrar la pareja: ${detail}`, {
        duration: 8000
      });
      setIsSubmitting(false);
    }
  };
  // ========================================================
  // HANDLER · Tenis
  // ========================================================
  const handleTenisRegistration = async () => {
    if (!selectedSlotTenis) {
      toast.error('Selecciona un número de equipo');
      return;
    }
    const filledPlayers = tenisPlayers.filter(p => p.name.trim());
    if (filledPlayers.length < 5) {
      toast.error('Registra a los 5 jugadores del equipo');
      return;
    }
    const captain = filledPlayers[0];
    if (!captain.name.trim() || !captain.email.trim() || !captain.phone.trim()) {
      toast.error('Completa los datos del Jugador 1 (capitán): nombre, correo y celular');
      return;
    }
    if (!acceptedTermsTenisAt || !acceptedReglamentoTenisAt || !acceptedHabeasTenisAt) {
      toast.error('Acepta los términos, el reglamento y el habeas data');
      return;
    }
    setIsSubmitting(true);
    const amountInCents = priceBreakdown(tenisTotal).total * 100;
    try {
      const {
        data: team,
        error: teamError
      } = await supabase.from('sports_team_registrations').insert({
        source_tag: SOURCE_TAG_TENIS,
        sport: 'tenis',
        category: 'tenis',
        team_slot: selectedSlotTenis,
        team_name: null,
        captain_name: captain.name.trim(),
        captain_email: captain.email.trim(),
        captain_phone: captain.phone.trim(),
        captain_cedula: captain.cedula.trim() || null,
        player_count: filledPlayers.length,
        amount_cents: amountInCents,
        payment_method: 'wompi',
        terms_version: TERMS_VERSION,
        ip_address: ipAddress,
        accepted_terms_at: acceptedTermsTenisAt,
        accepted_reglamento_at: acceptedReglamentoTenisAt,
        accepted_habeas_data_at: acceptedHabeasTenisAt
      }).select().single();
      if (teamError) {
        if (teamError.code === '23505') {
          toast.error('Ese número de equipo acaba de ser tomado. Por favor elige otro.');
          setIsSubmitting(false);
          return;
        }
        throw teamError;
      }
      if (!team) throw new Error('No se pudo crear el equipo');
      const playersPayload = filledPlayers.map((p, i) => ({
        team_id: team.id,
        player_index: i + 1,
        is_captain: i === 0,
        name: p.name.trim(),
        email: p.email.trim() || null,
        phone: p.phone.trim() || null,
        cedula: p.cedula.trim() || null
      }));
      const {
        error: playersError
      } = await supabase.from('sports_team_players').insert(playersPayload);
      if (playersError) {
        await supabase.from('sports_team_registrations').delete().eq('id', team.id);
        throw playersError;
      }
      toast.success('¡Equipo registrado! Redirigiendo al pago...');
      await redirectToWompi({
        reference: `TENIS-${team.id}`,
        amountInCents,
        customer: {
          email: captain.email.trim(),
          fullName: captain.name.trim(),
          phone: captain.phone.trim(),
          legalIdType: 'CC',
          legalId: captain.cedula.trim() || undefined
        },
        redirectUrl: `${window.location.origin}/gracias?id=${team.id}&table=sports_team_registrations`
      });
    } catch (err: any) {
      console.error('Error registrando equipo tenis:', err);
      const detail = err?.message || err?.details || err?.hint || 'error desconocido';
      toast.error(`No pudimos registrar el equipo: ${detail}`, {
        duration: 8000
      });
      setIsSubmitting(false);
    }
  };
  // ========================================================
  // Legal checkboxes block (reusable)
  // ========================================================
  const LegalBlock = ({
    termsAt,
    setTermsAt,
    reglamentoAt,
    setReglamentoAt,
    habeasAt,
    setHabeasAt
  }: {
    termsAt: string | null;
    setTermsAt: (v: string | null) => void;
    reglamentoAt: string | null;
    setReglamentoAt: (v: string | null) => void;
    habeasAt: string | null;
    setHabeasAt: (v: string | null) => void;
  }) => <div className="mt-6 space-y-2.5 bg-gray-50 rounded-2xl p-5 border border-gray-100" data-id="element-1726">
      <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer" data-id="element-1727">
        <input type="checkbox" checked={!!termsAt} onChange={e => toggleTs(e.target.checked, setTermsAt)} className="mt-1 w-4 h-4 accent-brand-cyan flex-shrink-0" data-id="element-1728" />
        <span data-id="element-1729">
          Acepto los{' '}
          <Link to="/terminos" target="_blank" className="text-brand-cyan underline hover:no-underline" data-id="element-1730">
            Términos y Condiciones
          </Link>
          .
        </span>
      </label>
      <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer" data-id="element-1731">
        <input type="checkbox" checked={!!reglamentoAt} onChange={e => toggleTs(e.target.checked, setReglamentoAt)} className="mt-1 w-4 h-4 accent-brand-cyan flex-shrink-0" data-id="element-1732" />
        <span data-id="element-1733">Acepto el reglamento del torneo.</span>
      </label>
      <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer" data-id="element-1734">
        <input type="checkbox" checked={!!habeasAt} onChange={e => toggleTs(e.target.checked, setHabeasAt)} className="mt-1 w-4 h-4 accent-brand-cyan flex-shrink-0" data-id="element-1735" />
        <span data-id="element-1736">
          Autorizo el tratamiento de mis datos personales (
          <Link to="/terminos#habeas" target="_blank" className="text-brand-cyan underline hover:no-underline" data-id="element-1737">
            Habeas Data
          </Link>
          ).
        </span>
      </label>
    </div>;
  const adultosLegalOk = !!acceptedTermsAdultosAt && !!acceptedReglamentoAdultosAt && !!acceptedHabeasAdultosAt;
  const ninosLegalOk = !!acceptedTermsNinosAt && !!acceptedReglamentoNinosAt && !!acceptedHabeasNinosAt;
  const padelLegalOk = !!acceptedTermsPadelAt && !!acceptedReglamentoPadelAt && !!acceptedHabeasPadelAt;
  const tenisLegalOk = !!acceptedTermsTenisAt && !!acceptedReglamentoTenisAt && !!acceptedHabeasTenisAt;
  return <div className="pt-20 bg-brand-navy" data-id="element-1738">
      <Toaster position="top-center" richColors data-id="element-1739" />

      {/* Hero */}
      <section className="relative py-24 bg-brand-navy overflow-hidden" data-id="element-1740">
        <div className="absolute inset-0" data-id="element-1741">
          <img src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=1920&q=80&auto=format&fit=crop" alt="" className="w-full h-full object-cover opacity-25" data-id="element-1742" />
          <div className="absolute inset-0 bg-brand-navy/70" data-id="element-1743"></div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1744">
          <Link to="/" className="inline-flex items-center gap-2 text-brand-cyan hover:text-white transition-colors mb-8 text-sm" data-id="element-1745">
            <ArrowLeft className="w-4 h-4" data-id="element-1746" /> Volver al inicio
          </Link>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-5xl md:text-7xl text-white mb-4 font-bold" data-id="element-1747">
            Zona <span className="text-brand-yellow" data-id="element-1748">Deportiva</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/80 max-w-2xl mx-auto" data-id="element-1749">
            Fútbol y pádel. Compite, diviértete y lleva a tu equipo a la gloria.
          </motion.p>
        </div>
      </section>

      {/* Sport Tabs */}
      <section className="py-16 bg-white" data-id="element-1750">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1751">
          <div className="text-center mb-10" data-id="element-1752">
            <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-4" data-id="element-1753">
              {['ELIGE', 'TU', 'DEPORTE'].map((word, i) => <motion.span key={i} initial={{
              opacity: 0,
              y: 30,
              filter: 'blur(10px)'
            }} whileInView={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: i * 0.15,
              ease: [0.22, 1, 0.36, 1]
            }} className="inline-block shimmer-text mr-3" data-id="element-1754">
                  {word}
                </motion.span>)}
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto" data-id="element-1755">
              Selecciona la disciplina en la que quieres competir y registra tu
              equipo.
            </p>
          </div>

          <div className="flex justify-center mb-12" data-id="element-1756">
            <motion.div initial={{
            opacity: 0,
            y: -20,
            scale: 0.85
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 18,
            delay: 0.4
          }} className="relative" data-id="element-1757">
              <div className="relative bg-gray-100 p-1.5 rounded-full flex gap-2" data-id="element-1758">
                {(['futbol', 'tenis', 'padel'] as Sport[]).map(sport => {
                const isActive = activeSport === sport;
                return <button key={sport} onClick={() => setActiveSport(sport)} className={`relative px-8 py-3 rounded-full font-bold text-lg transition-colors z-10 ${isActive ? 'text-white' : 'text-gray-500 hover:text-brand-navy'}`} data-id="element-1759">
                      {isActive && <motion.span layoutId="sport-tab-indicator" className="absolute inset-0 bg-brand-navy rounded-full shadow-lg" transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 32
                  }} data-id="element-1760" />}
                      <span className="relative z-10 inline-flex items-center gap-2" data-id="element-1761">
                        {sport === 'futbol' ? '⚽ Fútbol' : sport === 'tenis' ? <>
                            <img src="https://cdn.magicpatterns.com/uploads/gwchGqFrM23u2gzUXcWaVR/3d-pelota-tenis-aislado_1308-121223.png" alt="" aria-hidden="true" className="w-6 h-6 object-contain" data-id="element-1762" />
                            Tenis
                          </> : '🎾 Pádel'}
                      </span>
                    </button>;
              })}
              </div>
            </motion.div>
          </div>

          {/* Fútbol Section */}
          {activeSport === 'futbol' && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} key="futbol" data-id="element-1763">
              {/* Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-1764">
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1765">
                  <Trophy className="w-10 h-10 text-brand-yellow mx-auto mb-3" data-id="element-1766" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1767">3 Canchas</h3>
                  <p className="text-gray-500 text-sm" data-id="element-1768">Canchas profesionales</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1769">
                  <Users className="w-10 h-10 text-brand-cyan mx-auto mb-3" data-id="element-1770" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1771">
                    Equipos Adultos y Niños
                  </h3>
                  <p className="text-gray-500 text-sm" data-id="element-1772">
                    Inscripción por equipo con capitán
                  </p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1773">
                  <Clock className="w-10 h-10 text-brand-green mx-auto mb-3" data-id="element-1774" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1775">
                    8:00 AM - 4:00 PM
                  </h3>
                  <p className="text-gray-500 text-sm" data-id="element-1776">Bracket automático</p>
                </div>
              </div>

              {/* Category sub-tabs */}
              <div className="flex justify-center mb-8" data-id="element-1777">
                <motion.div initial={{
              scale: 0.9,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1,
              boxShadow: ['0 4px 14px 0 rgba(0, 188, 212, 0.25)', '0 8px 24px 0 rgba(0, 188, 212, 0.45)', '0 4px 14px 0 rgba(0, 188, 212, 0.25)']
            }} transition={{
              scale: {
                type: 'spring',
                stiffness: 300,
                damping: 18
              },
              opacity: {
                duration: 0.4
              },
              boxShadow: {
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeInOut'
              }
            }} className="relative bg-white p-1.5 rounded-full flex gap-2 border-2 border-brand-cyan/40" data-id="element-1778">
                  {([{
                id: 'adultos',
                label: 'Adultos',
                icon: User,
                price: ADULTO_PRICE
              }, {
                id: 'ninos',
                label: 'Niños',
                icon: Baby,
                price: NINO_PRICE
              }] as {
                id: FutbolCategory;
                label: string;
                icon: any;
                price: number;
              }[]).map(cat => {
                const isActive = futbolCategory === cat.id;
                const Icon = cat.icon;
                return <button key={cat.id} onClick={() => setFutbolCategory(cat.id)} className={`relative px-6 py-2.5 rounded-full font-bold text-sm md:text-base transition-colors z-10 flex items-center gap-2 ${isActive ? 'text-white' : 'text-gray-500 hover:text-brand-navy'}`} data-id="element-1779">
                        {isActive && <motion.span layoutId="futbol-cat-indicator" className="absolute inset-0 bg-brand-cyan rounded-full shadow-md" transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 32
                  }} data-id="element-1780" />}
                        <span className="relative z-10 flex items-center gap-2" data-id="element-1781">
                          <Icon className="w-4 h-4" data-id="element-1782" />
                          {cat.label}
                          <span className="text-xs font-semibold opacity-80" data-id="element-1783">
                            ${cat.price.toLocaleString('es-CO')}
                          </span>
                        </span>
                      </button>;
              })}
                </motion.div>
              </div>

              {/* Adultos Form */}
              {futbolCategory === 'adultos' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} key="adultos" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100" data-id="element-1784">
                  <div className="text-center mb-8" data-id="element-1785">
                    <h2 className="text-3xl font-bold text-brand-navy mb-2" data-id="element-1786">
                      Inscripción Equipo · Adultos
                    </h2>
                    <p className="text-gray-500" data-id="element-1787">
                      ${ADULTO_PRICE.toLocaleString('es-CO')} por jugador ·
                      Mínimo 6 jugadores
                    </p>
                  </div>

                  {/* Slot Selector */}
                  <div className="mb-8" data-id="element-1788">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1789">
                      Elige tu número de equipo ({6 - takenSlotsAdultos.length}{' '}
                      de 6 disponibles) *
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-1790">
                      {[1, 2, 3, 4, 5, 6].map(slot => {
                  const isTaken = takenSlotsAdultos.includes(slot);
                  const isSelected = selectedSlotAdultos === slot;
                  return <button key={slot} type="button" disabled={isTaken} onClick={() => setSelectedSlotAdultos(slot)} className={`relative flex-1 min-w-[80px] py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-1.5
                              ${isTaken ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : isSelected ? 'bg-brand-cyan border-brand-cyan text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-cyan/50 hover:bg-brand-cyan/5'}`} data-id="element-1791">
                            {isTaken && <span className="text-[10px]" data-id="element-1792">🔒</span>}
                            Equipo {slot}
                          </button>;
                })}
                    </div>
                  </div>

                  {/* Team name */}
                  <div className="mb-6" data-id="element-1793">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1794">
                      Nombre del equipo *
                    </label>
                    <input type="text" value={teamNameAdultos} onChange={e => setTeamNameAdultos(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none" placeholder="Ej. Los Tigres FC" data-id="element-1795" />
                  </div>

                  {/* Captain */}
                  <div className="mb-8 bg-brand-navy/5 rounded-2xl p-5 border border-brand-navy/10" data-id="element-1796">
                    <p className="text-xs uppercase tracking-wider font-bold text-brand-navy mb-3 flex items-center gap-2" data-id="element-1797">
                      <Crown className="w-4 h-4 text-brand-yellow" data-id="element-1798" /> Capitán
                      del equipo
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1799">
                      <input type="text" value={captainAdultos.name} onChange={e => updateCaptainAdultos('name', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Nombre completo" data-id="element-1800" />
                      <input type="email" value={captainAdultos.email} onChange={e => updateCaptainAdultos('email', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Correo electrónico" data-id="element-1801" />
                      <input type="tel" value={captainAdultos.phone} onChange={e => updateCaptainAdultos('phone', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Celular / WhatsApp" data-id="element-1802" />
                      <input type="text" value={captainAdultos.cedula} onChange={e => updateCaptainAdultos('cedula', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Nro. de cédula" data-id="element-1803" />
                    </div>
                  </div>

                  {/* Players */}
                  <p className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-3" data-id="element-1804">
                    Jugadores
                  </p>
                  <div className="space-y-3" data-id="element-1805">
                    {adultos.map((player, i) => <div key={i} className="bg-gray-50 rounded-2xl p-4 border border-gray-100 relative" data-id="element-1806">
                        <div className="flex items-center justify-between mb-3" data-id="element-1807">
                          <div className="flex items-center gap-2" data-id="element-1808">
                            <span className="w-7 h-7 rounded-full bg-brand-navy text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-1809">
                              {i + 1}
                            </span>
                            <span className="text-sm font-semibold text-brand-navy" data-id="element-1810">
                              Jugador {i + 1}
                            </span>
                          </div>
                          {adultos.length > 1 && <button onClick={() => removeAdulto(i)} className="text-gray-400 hover:text-red-500 transition-colors text-sm font-medium flex items-center gap-1" data-id="element-1811">
                              <Minus className="w-4 h-4" data-id="element-1812" /> Quitar
                            </button>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-1813">
                          <input type="text" value={player.name} onChange={e => updateAdulto(i, 'name', e.target.value)} className="md:col-span-2 px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nombre completo" data-id="element-1814" />
                          <input type="number" min="18" max="99" value={player.age} onChange={e => updateAdulto(i, 'age', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Edad" data-id="element-1815" />
                          <input type="text" value={player.cedula} onChange={e => updateAdulto(i, 'cedula', e.target.value)} className="md:col-span-3 px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nro. de cédula" data-id="element-1816" />
                        </div>
                      </div>)}
                  </div>

                  <button onClick={addAdulto} disabled={adultos.length >= 10} className="mt-4 w-full py-3 border-2 border-dashed border-brand-cyan/40 rounded-2xl text-brand-cyan font-bold text-sm hover:bg-brand-cyan/5 transition-colors flex items-center justify-center gap-2 disabled:opacity-30" data-id="element-1817">
                    <Plus className="w-4 h-4" data-id="element-1818" /> Agregar otro jugador
                  </button>

                  {/* Total */}
                  <div className="mt-8 bg-brand-navy/5 rounded-2xl p-5" data-id="element-1819">
                    <div className="flex items-start justify-between mb-3" data-id="element-1820">
                      <p className="text-sm text-gray-500" data-id="element-1821">
                        {adultos.length} jugador
                        {adultos.length !== 1 ? 'es' : ''} ×{' '}
                        {formatCOP(ADULTO_PRICE)}
                      </p>
                      <Trophy className="w-12 h-12 text-brand-yellow" data-id="element-1822" />
                    </div>
                    <div className="space-y-1.5 text-sm border-t border-brand-navy/10 pt-3" data-id="element-1823">
                      <div className="flex justify-between text-gray-600" data-id="element-1824">
                        <span data-id="element-1825">Subtotal</span>
                        <span className="font-medium" data-id="element-1826">
                          {formatCOP(adultosTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600" data-id="element-1827">
                        <span data-id="element-1828">IVA (19%)</span>
                        <span className="font-medium" data-id="element-1829">
                          {formatCOP(priceBreakdown(adultosTotal).iva)}
                        </span>
                      </div>
                      <div className="flex justify-between text-brand-navy pt-2 border-t border-brand-navy/10 mt-1" data-id="element-1830">
                        <span className="font-bold" data-id="element-1831">Total a pagar</span>
                        <span className="font-black text-xl" data-id="element-1832">
                          {formatCOP(priceBreakdown(adultosTotal).total)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Legal */}
                  <LegalBlock termsAt={acceptedTermsAdultosAt} setTermsAt={setAcceptedTermsAdultosAt} reglamentoAt={acceptedReglamentoAdultosAt} setReglamentoAt={setAcceptedReglamentoAdultosAt} habeasAt={acceptedHabeasAdultosAt} setHabeasAt={setAcceptedHabeasAdultosAt} data-id="element-1833" />

                  <button onClick={handleAdultosRegistration} disabled={isSubmitting || !adultosLegalOk} className="mt-6 w-full bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-full font-bold text-lg transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1834">
                    {isSubmitting ? 'Registrando...' : `Registrar equipo · Pagar ${formatCOP(priceBreakdown(adultosTotal).total)}`}
                  </button>
                  <p className="text-center text-gray-400 text-xs mt-3" data-id="element-1835">
                    Tu equipo se registra y serás redirigido al pago.
                  </p>
                </motion.div>}

              {/* Niños Form */}
              {futbolCategory === 'ninos' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} key="ninos" className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100" data-id="element-1836">
                  <div className="text-center mb-8" data-id="element-1837">
                    <h2 className="text-3xl font-bold text-brand-navy mb-2" data-id="element-1838">
                      Inscripción Equipo · Niños
                    </h2>
                    <p className="text-gray-500" data-id="element-1839">
                      ${NINO_PRICE.toLocaleString('es-CO')} por niño · Cada niño
                      debe tener un adulto responsable
                    </p>
                  </div>

                  {/* Slot Selector */}
                  <div className="mb-8" data-id="element-1840">
                    <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1841">
                      Elige tu número de equipo ({6 - takenSlotsNinos.length} de
                      6 disponibles) *
                    </label>
                    <div className="flex flex-wrap gap-3" data-id="element-1842">
                      {[1, 2, 3, 4, 5, 6].map(slot => {
                  const isTaken = takenSlotsNinos.includes(slot);
                  const isSelected = selectedSlotNinos === slot;
                  return <button key={slot} type="button" disabled={isTaken} onClick={() => setSelectedSlotNinos(slot)} className={`relative flex-1 min-w-[80px] py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-1.5
                              ${isTaken ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : isSelected ? 'bg-brand-cyan border-brand-cyan text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-cyan/50 hover:bg-brand-cyan/5'}`} data-id="element-1843">
                            {isTaken && <span className="text-[10px]" data-id="element-1844">🔒</span>}
                            Equipo {slot}
                          </button>;
                })}
                    </div>
                  </div>

                  {/* Team name */}
                  <div className="mb-6" data-id="element-1845">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1846">
                      Nombre del equipo *
                    </label>
                    <input type="text" value={teamNameNinos} onChange={e => setTeamNameNinos(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none" placeholder="Ej. Los Cachorros FC" data-id="element-1847" />
                  </div>

                  {/* Captain */}
                  <div className="mb-8 bg-brand-navy/5 rounded-2xl p-5 border border-brand-navy/10" data-id="element-1848">
                    <p className="text-xs uppercase tracking-wider font-bold text-brand-navy mb-3 flex items-center gap-2" data-id="element-1849">
                      <Crown className="w-4 h-4 text-brand-yellow" data-id="element-1850" /> Capitán /
                      Adulto a cargo del equipo
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1851">
                      <input type="text" value={captainNinos.name} onChange={e => updateCaptainNinos('name', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Nombre completo" data-id="element-1852" />
                      <input type="email" value={captainNinos.email} onChange={e => updateCaptainNinos('email', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Correo electrónico" data-id="element-1853" />
                      <input type="tel" value={captainNinos.phone} onChange={e => updateCaptainNinos('phone', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Celular / WhatsApp" data-id="element-1854" />
                      <input type="text" value={captainNinos.cedula} onChange={e => updateCaptainNinos('cedula', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm bg-white" placeholder="Nro. de cédula" data-id="element-1855" />
                    </div>
                  </div>

                  {/* Players */}
                  <p className="text-xs uppercase tracking-wider font-bold text-gray-500 mb-3" data-id="element-1856">
                    Jugadores (niños)
                  </p>
                  <div className="space-y-4" data-id="element-1857">
                    {ninos.map((player, i) => <div key={i} className="bg-gray-50 rounded-2xl p-5 border border-gray-100" data-id="element-1858">
                        <div className="flex items-center justify-between mb-3" data-id="element-1859">
                          <div className="flex items-center gap-2" data-id="element-1860">
                            <span className="w-7 h-7 rounded-full bg-brand-yellow text-brand-navy flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-1861">
                              {i + 1}
                            </span>
                            <span className="text-sm font-semibold text-brand-navy" data-id="element-1862">
                              Niño {i + 1}
                            </span>
                          </div>
                          {ninos.length > 1 && <button onClick={() => removeNino(i)} className="text-gray-400 hover:text-red-500 transition-colors text-sm font-medium flex items-center gap-1" data-id="element-1863">
                              <Minus className="w-4 h-4" data-id="element-1864" /> Quitar
                            </button>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-id="element-1865">
                          <input type="text" value={player.name} onChange={e => updateNino(i, 'name', e.target.value)} className="md:col-span-2 px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nombre completo del niño" data-id="element-1866" />
                          <input type="number" min="5" max="17" value={player.age} onChange={e => updateNino(i, 'age', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Edad" data-id="element-1867" />
                          <input type="text" value={player.ti} onChange={e => updateNino(i, 'ti', e.target.value)} className="md:col-span-3 px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nro. de Tarjeta de Identidad (T.I.)" data-id="element-1868" />
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-1869">
                          <p className="text-xs uppercase tracking-wider font-bold text-amber-800 mb-2 flex items-center gap-2" data-id="element-1870">
                            <User className="w-3.5 h-3.5" data-id="element-1871" /> Adulto responsable
                            de este niño
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1872">
                            <input type="text" value={player.responsableName} onChange={e => updateNino(i, 'responsableName', e.target.value)} className="px-4 py-2.5 rounded-xl border border-amber-200 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent outline-none text-sm" placeholder="Nombre del responsable" data-id="element-1873" />
                            <input type="tel" value={player.responsablePhone} onChange={e => updateNino(i, 'responsablePhone', e.target.value)} className="px-4 py-2.5 rounded-xl border border-amber-200 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent outline-none text-sm" placeholder="Teléfono del responsable" data-id="element-1874" />
                          </div>
                        </div>
                      </div>)}
                  </div>

                  <button onClick={addNino} disabled={ninos.length >= 8} className="mt-4 w-full py-3 border-2 border-dashed border-brand-cyan/40 rounded-2xl text-brand-cyan font-bold text-sm hover:bg-brand-cyan/5 transition-colors flex items-center justify-center gap-2 disabled:opacity-30" data-id="element-1875">
                    <Plus className="w-4 h-4" data-id="element-1876" /> Agregar otro niño
                  </button>

                  {/* Total */}
                  <div className="mt-8 bg-brand-navy/5 rounded-2xl p-5" data-id="element-1877">
                    <div className="flex items-start justify-between mb-3" data-id="element-1878">
                      <p className="text-sm text-gray-500" data-id="element-1879">
                        {ninos.length} niño{ninos.length !== 1 ? 's' : ''} ×{' '}
                        {formatCOP(NINO_PRICE)}
                      </p>
                      <Baby className="w-12 h-12 text-brand-yellow" data-id="element-1880" />
                    </div>
                    <div className="space-y-1.5 text-sm border-t border-brand-navy/10 pt-3" data-id="element-1881">
                      <div className="flex justify-between text-gray-600" data-id="element-1882">
                        <span data-id="element-1883">Subtotal</span>
                        <span className="font-medium" data-id="element-1884">
                          {formatCOP(ninosTotal)}
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600" data-id="element-1885">
                        <span data-id="element-1886">IVA (19%)</span>
                        <span className="font-medium" data-id="element-1887">
                          {formatCOP(priceBreakdown(ninosTotal).iva)}
                        </span>
                      </div>
                      <div className="flex justify-between text-brand-navy pt-2 border-t border-brand-navy/10 mt-1" data-id="element-1888">
                        <span className="font-bold" data-id="element-1889">Total a pagar</span>
                        <span className="font-black text-xl" data-id="element-1890">
                          {formatCOP(priceBreakdown(ninosTotal).total)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Legal */}
                  <LegalBlock termsAt={acceptedTermsNinosAt} setTermsAt={setAcceptedTermsNinosAt} reglamentoAt={acceptedReglamentoNinosAt} setReglamentoAt={setAcceptedReglamentoNinosAt} habeasAt={acceptedHabeasNinosAt} setHabeasAt={setAcceptedHabeasNinosAt} data-id="element-1891" />

                  <button onClick={handleNinosRegistration} disabled={isSubmitting || !ninosLegalOk} className="mt-6 w-full bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-full font-bold text-lg transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1892">
                    {isSubmitting ? 'Registrando...' : `Registrar equipo · Pagar ${formatCOP(priceBreakdown(ninosTotal).total)}`}
                  </button>
                  <p className="text-center text-gray-400 text-xs mt-3" data-id="element-1893">
                    Tu equipo se registra y serás redirigido al pago.
                  </p>
                </motion.div>}
            </motion.div>}

          {/* Pádel Section */}
          {activeSport === 'padel' && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} key="padel" data-id="element-1894">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-1895">
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1896">
                  <MapPin className="w-10 h-10 text-brand-cyan mx-auto mb-3" data-id="element-1897" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1898">
                    2 Canchas de Pádel
                  </h3>
                  <p className="text-gray-500 text-sm" data-id="element-1899">+ 1 cancha de tenis</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1900">
                  <Users className="w-10 h-10 text-brand-yellow mx-auto mb-3" data-id="element-1901" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1902">Parejas</h3>
                  <p className="text-gray-500 text-sm" data-id="element-1903">
                    Torneo por parejas (2 jugadores)
                  </p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1904">
                  <Trophy className="w-10 h-10 text-brand-green mx-auto mb-3" data-id="element-1905" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1906">
                    Jueces Profesionales
                  </h3>
                  <p className="text-gray-500 text-sm" data-id="element-1907">Arbitraje certificado</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100" data-id="element-1908">
                <h2 className="text-3xl font-bold text-brand-navy mb-2 text-center" data-id="element-1909">
                  Registra tu Pareja
                </h2>
                <p className="text-gray-500 text-center mb-8" data-id="element-1910">
                  Inscripción por parejas · 2 jugadores · $
                  {PADEL_PRICE.toLocaleString('es-CO')} por jugador
                </p>

                <div className="space-y-6" data-id="element-1911">
                  <div data-id="element-1912">
                    <div className="flex justify-between items-center mb-4" data-id="element-1913">
                      <label className="text-sm font-semibold text-gray-700" data-id="element-1914">
                        Jugadores (2)
                      </label>
                    </div>
                    <div className="space-y-4" data-id="element-1915">
                      {padelPlayers.map((player, i) => <div key={i} className={`rounded-2xl p-5 border ${i === 0 ? 'bg-brand-yellow/10 border-brand-yellow/40' : 'bg-gray-50 border-gray-100'}`} data-id="element-1916">
                          <div className="flex items-center gap-2 mb-3" data-id="element-1917">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-brand-yellow text-brand-navy' : 'bg-brand-navy text-white'}`} data-id="element-1918">
                              {i === 0 ? <Crown className="w-3.5 h-3.5" data-id="element-1919" /> : i + 1}
                            </span>
                            <span className="text-sm font-semibold text-brand-navy" data-id="element-1920">
                              {i === 0 ? 'Jugador 1 · Capitán' : `Jugador ${i + 1}`}
                            </span>
                            {i === 0 && <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-brand-yellow bg-brand-yellow/20 px-2 py-0.5 rounded-full" data-id="element-1921">
                                Identifica al equipo
                              </span>}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1922">
                            <input type="text" value={player.name} onChange={e => updatePadelPlayer(i, 'name', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nombre completo" data-id="element-1923" />
                            <input type="email" value={player.email} onChange={e => updatePadelPlayer(i, 'email', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Correo electrónico" data-id="element-1924" />
                            <input type="tel" value={player.phone} onChange={e => updatePadelPlayer(i, 'phone', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Número de celular" data-id="element-1925" />
                            <input type="text" value={player.cedula} onChange={e => updatePadelPlayer(i, 'cedula', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nro. de cédula" data-id="element-1926" />
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="mt-8 bg-brand-navy/5 rounded-2xl p-5" data-id="element-1927">
                  <div className="flex items-start justify-between mb-3" data-id="element-1928">
                    <p className="text-sm text-gray-500" data-id="element-1929">
                      {padelPlayers.length} jugadores × {formatCOP(PADEL_PRICE)}
                    </p>
                    <Trophy className="w-12 h-12 text-brand-yellow" data-id="element-1930" />
                  </div>
                  <div className="space-y-1.5 text-sm border-t border-brand-navy/10 pt-3" data-id="element-1931">
                    <div className="flex justify-between text-gray-600" data-id="element-1932">
                      <span data-id="element-1933">Subtotal</span>
                      <span className="font-medium" data-id="element-1934">
                        {formatCOP(padelTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600" data-id="element-1935">
                      <span data-id="element-1936">IVA (19%)</span>
                      <span className="font-medium" data-id="element-1937">
                        {formatCOP(priceBreakdown(padelTotal).iva)}
                      </span>
                    </div>
                    <div className="flex justify-between text-brand-navy pt-2 border-t border-brand-navy/10 mt-1" data-id="element-1938">
                      <span className="font-bold" data-id="element-1939">Total a pagar</span>
                      <span className="font-black text-xl" data-id="element-1940">
                        {formatCOP(priceBreakdown(padelTotal).total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Legal */}
                <LegalBlock termsAt={acceptedTermsPadelAt} setTermsAt={setAcceptedTermsPadelAt} reglamentoAt={acceptedReglamentoPadelAt} setReglamentoAt={setAcceptedReglamentoPadelAt} habeasAt={acceptedHabeasPadelAt} setHabeasAt={setAcceptedHabeasPadelAt} data-id="element-1941" />

                <button onClick={handlePadelRegistration} disabled={isSubmitting || !padelLegalOk} className="mt-6 w-full block text-center bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-full font-bold text-lg transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1942">
                  {isSubmitting ? 'Registrando...' : `Registrar · Pagar ${formatCOP(priceBreakdown(padelTotal).total)}`}
                </button>
                <p className="text-center text-gray-400 text-xs mt-3" data-id="element-1943">
                  Tu inscripción se registra y serás redirigido al pago.
                </p>
              </div>
            </motion.div>}

          {/* Tenis Section */}
          {activeSport === 'tenis' && <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} key="tenis" data-id="element-1944">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-1945">
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1946">
                  <MapPin className="w-10 h-10 text-brand-cyan mx-auto mb-3" data-id="element-1947" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1948">
                    1 Cancha de Tenis
                  </h3>
                  <p className="text-gray-500 text-sm" data-id="element-1949">Cancha profesional</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1950">
                  <Users className="w-10 h-10 text-brand-yellow mx-auto mb-3" data-id="element-1951" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1952">Equipos</h3>
                  <p className="text-gray-500 text-sm" data-id="element-1953">
                    Torneo por equipos (5 jugadores)
                  </p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-6 text-center border border-gray-100" data-id="element-1954">
                  <Trophy className="w-10 h-10 text-brand-green mx-auto mb-3" data-id="element-1955" />
                  <h3 className="font-bold text-brand-navy mb-1" data-id="element-1956">
                    Jueces Profesionales
                  </h3>
                  <p className="text-gray-500 text-sm" data-id="element-1957">Arbitraje certificado</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100" data-id="element-1958">
                <h2 className="text-3xl font-bold text-brand-navy mb-2 text-center" data-id="element-1959">
                  Registra tu Equipo
                </h2>
                <p className="text-gray-500 text-center mb-8" data-id="element-1960">
                  Inscripción por equipos · 5 jugadores · $
                  {TENIS_PRICE.toLocaleString('es-CO')} por jugador
                </p>

                {/* Slot Selector */}
                <div className="mb-8" data-id="element-1961">
                  <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1962">
                    Elige tu número de equipo ({2 - takenSlotsTenis.length} de 2
                    disponibles) *
                  </label>
                  <div className="flex flex-wrap gap-3" data-id="element-1963">
                    {[1, 2].map(slot => {
                  const isTaken = takenSlotsTenis.includes(slot);
                  const isSelected = selectedSlotTenis === slot;
                  return <button key={slot} type="button" disabled={isTaken} onClick={() => setSelectedSlotTenis(slot)} className={`relative flex-1 min-w-[80px] py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-1.5
                            ${isTaken ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : isSelected ? 'bg-brand-cyan border-brand-cyan text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-cyan/50 hover:bg-brand-cyan/5'}`} data-id="element-1964">
                          {isTaken && <span className="text-[10px]" data-id="element-1965">🔒</span>}
                          Equipo {slot}
                        </button>;
                })}
                  </div>
                </div>

                <div className="space-y-6" data-id="element-1966">
                  <div data-id="element-1967">
                    <div className="flex justify-between items-center mb-4" data-id="element-1968">
                      <label className="text-sm font-semibold text-gray-700" data-id="element-1969">
                        Jugadores (5)
                      </label>
                    </div>
                    <div className="space-y-4" data-id="element-1970">
                      {tenisPlayers.map((player, i) => <div key={i} className={`rounded-2xl p-5 border ${i === 0 ? 'bg-brand-yellow/10 border-brand-yellow/40' : 'bg-gray-50 border-gray-100'}`} data-id="element-1971">
                          <div className="flex items-center gap-2 mb-3" data-id="element-1972">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${i === 0 ? 'bg-brand-yellow text-brand-navy' : 'bg-brand-navy text-white'}`} data-id="element-1973">
                              {i === 0 ? <Crown className="w-3.5 h-3.5" data-id="element-1974" /> : i + 1}
                            </span>
                            <span className="text-sm font-semibold text-brand-navy" data-id="element-1975">
                              {i === 0 ? 'Jugador 1 · Capitán' : `Jugador ${i + 1}`}
                            </span>
                            {i === 0 && <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-brand-yellow bg-brand-yellow/20 px-2 py-0.5 rounded-full" data-id="element-1976">
                                Identifica al equipo
                              </span>}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1977">
                            <input type="text" value={player.name} onChange={e => updateTenisPlayer(i, 'name', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nombre completo" data-id="element-1978" />
                            <input type="email" value={player.email} onChange={e => updateTenisPlayer(i, 'email', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Correo electrónico" data-id="element-1979" />
                            <input type="tel" value={player.phone} onChange={e => updateTenisPlayer(i, 'phone', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Número de celular" data-id="element-1980" />
                            <input type="text" value={player.cedula} onChange={e => updateTenisPlayer(i, 'cedula', e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-cyan focus:border-transparent outline-none text-sm" placeholder="Nro. de cédula" data-id="element-1981" />
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="mt-8 bg-brand-navy/5 rounded-2xl p-5" data-id="element-1982">
                  <div className="flex items-start justify-between mb-3" data-id="element-1983">
                    <p className="text-sm text-gray-500" data-id="element-1984">
                      {tenisPlayers.length} jugadores × {formatCOP(TENIS_PRICE)}
                    </p>
                    <Trophy className="w-12 h-12 text-brand-yellow" data-id="element-1985" />
                  </div>
                  <div className="space-y-1.5 text-sm border-t border-brand-navy/10 pt-3" data-id="element-1986">
                    <div className="flex justify-between text-gray-600" data-id="element-1987">
                      <span data-id="element-1988">Subtotal</span>
                      <span className="font-medium" data-id="element-1989">
                        {formatCOP(tenisTotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-gray-600" data-id="element-1990">
                      <span data-id="element-1991">IVA (19%)</span>
                      <span className="font-medium" data-id="element-1992">
                        {formatCOP(priceBreakdown(tenisTotal).iva)}
                      </span>
                    </div>
                    <div className="flex justify-between text-brand-navy pt-2 border-t border-brand-navy/10 mt-1" data-id="element-1993">
                      <span className="font-bold" data-id="element-1994">Total a pagar</span>
                      <span className="font-black text-xl" data-id="element-1995">
                        {formatCOP(priceBreakdown(tenisTotal).total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Legal */}
                <LegalBlock termsAt={acceptedTermsTenisAt} setTermsAt={setAcceptedTermsTenisAt} reglamentoAt={acceptedReglamentoTenisAt} setReglamentoAt={setAcceptedReglamentoTenisAt} habeasAt={acceptedHabeasTenisAt} setHabeasAt={setAcceptedHabeasTenisAt} data-id="element-1996" />

                <button onClick={handleTenisRegistration} disabled={isSubmitting || !tenisLegalOk} className="mt-6 w-full block text-center bg-brand-cyan hover:bg-blue-700 text-white py-4 rounded-full font-bold text-lg transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1997">
                  {isSubmitting ? 'Registrando...' : `Registrar · Pagar ${formatCOP(priceBreakdown(tenisTotal).total)}`}
                </button>
                <p className="text-center text-gray-400 text-xs mt-3" data-id="element-1998">
                  Tu inscripción se registra y serás redirigido al pago.
                </p>
              </div>
            </motion.div>}
        </div>
      </section>
    </div>;
}